import axios from 'axios';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { DriverScore, DriverScoreDetail, defaultValue } from 'app/shared/model/driver-score.model';
import { PeriodicType } from 'app/shared/model/enumerations/periodicType';
import { toRFC3339UTC } from 'app/shared/util/date-utils';

const initialState: EntityState<DriverScore> & { detail: DriverScoreDetail | null } = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
  detail: null,
};

export interface DriverScoreQueryProps {
  companyId?: number | null;
  periodicType: PeriodicType;
  dateStart?: string | null;
  dateEnd?: string | null;
  page?: number | null;
  size?: number | null;
  sortDirection?: string | null;
  searchDriver?: string | null;
}

export interface DriverScoreDetailProps {
  userId: number;
  periodicType: PeriodicType;
  dateStart?: string | null;
  dateEnd?: string | null;
}

const apiUrl = 'api/control-tower/driver-scores';

let cancelTokenSource = axios.CancelToken.source();

export const getDriverScores = createAsyncThunk(
  'driver-classification/fetch_driver_scores',
  async ({ companyId, periodicType, dateStart, dateEnd, page, size, sortDirection, searchDriver }: DriverScoreQueryProps) => {
    const queryParams = new URLSearchParams();

    if (periodicType === PeriodicType.CUSTOM && dateStart && dateEnd) {
      queryParams.append('dateStart', toRFC3339UTC(dateStart));
      queryParams.append('dateEnd', toRFC3339UTC(dateEnd));
    }

    if (page !== null && page !== undefined) {
      queryParams.append('page', page.toString());
    }

    if (size !== null && size !== undefined) {
      queryParams.append('size', size.toString());
    }

    if (sortDirection) {
      queryParams.append('sortDirection', sortDirection);
    }

    if (searchDriver && searchDriver.trim() !== '') {
      queryParams.append('searchDriver', searchDriver.trim());
    }

    if (companyId !== null && companyId !== undefined) {
      queryParams.append('searchCompanyId', companyId.toString());
    }

    if (cancelTokenSource) {
      cancelTokenSource.cancel('Driver score request superseded');
    }
    cancelTokenSource = axios.CancelToken.source();

    const requestUrl = `${apiUrl}/${periodicType}?${queryParams.toString()}`;
    const response = await axios.get<any>(requestUrl, { cancelToken: cancelTokenSource.token });
    return response.data;
  },
  { serializeError: serializeAxiosError }
);

export const getDriverScoreDetail = createAsyncThunk(
  'driver-classification/fetch_driver_score_detail',
  async ({ userId, periodicType, dateStart, dateEnd }: DriverScoreDetailProps) => {
    const queryParams = new URLSearchParams();

    if (periodicType === PeriodicType.CUSTOM && dateStart && dateEnd) {
      queryParams.append('dateStart', toRFC3339UTC(dateStart));
      queryParams.append('dateEnd', toRFC3339UTC(dateEnd));
    }

    const requestUrl = `${apiUrl}/${periodicType}/${userId}?${queryParams.toString()}`;
    const response = await axios.get<DriverScoreDetail>(requestUrl);
    return response.data;
  },
  { serializeError: serializeAxiosError }
);

const driverControlTowerSlice = createSlice({
  name: 'driverControlTower',
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
    clearDetail(state) {
      state.detail = null;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getDriverScores.fulfilled, (state, action) => {
        state.loading = false;
        state.errorMessage = null;
        state.entities = action.payload?.content ?? [];
        state.totalItems = action.payload?.totalElements ?? 0;
      })
      .addCase(getDriverScores.pending, state => {
        state.errorMessage = null;
        state.loading = true;
      })
      .addCase(getDriverScores.rejected, (state, action) => {
        if (action.error?.name === 'CanceledError' || action.error?.message === 'Driver score request superseded') {
          return;
        }
        state.loading = false;
        state.errorMessage = action.error.message || 'Error fetching data';
      })
      .addCase(getDriverScoreDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.detail = action.payload;
      })
      .addCase(getDriverScoreDetail.pending, state => {
        state.loading = true;
      })
      .addCase(getDriverScoreDetail.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.error.message || 'Error fetching data';
      });
  },
});

export const { reset, clearDetail } = driverControlTowerSlice.actions;

export default driverControlTowerSlice.reducer;
